import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Single hook that drives the whole screen. Keeps Supabase calls out of components.
// Returns: items, settings, todayLogs, todayTotal, yesterdayTotal, top3, loading, refresh.
export function useWasteData() {
  const [items, setItems] = useState([])
  const [settings, setSettings] = useState({
    daily_target: 100,
    large_entry_threshold: 50,
  })
  const [todayLogs, setTodayLogs] = useState([])
  const [yesterdayTotal, setYesterdayTotal] = useState(0)
  // Headline savings summary — fed into the LifetimeSavings tile.
  // Defaults are the empty state from vw_savings_summary on a fresh DB.
  const [savingsSummary, setSavingsSummary] = useState({
    confirmed_total: 0,
    preliminary_total: 0,
    lifetime_total: 0,
    confirmed_count: 0,
    event_count: 0,
    last_event_at: null,
    today_total: 0,
    last_event_source: null,
  })
  // All recent savings events. Powers two UIs:
  //   - SavingsBreakdownSheet (tap-the-tile breakdown — needs everything)
  //   - OwnerAttestationModal (only unconfirmed — derived via filter)
  // Capped at 500 to keep the payload small; paginate if pilot ever
  // produces more than ~year of daily events.
  const [savingsEvents, setSavingsEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)

    // Kitchen-day boundaries — computed fresh per fetch so a tab left
    // open across midnight doesn't show stale "yesterday" data. Browser
    // local time; assumes the browser TZ matches the kitchen, true for
    // the single-restaurant BBQ pilot. Pin to America/Chicago explicitly
    // when we go multi-restaurant.
    const now = new Date()
    const todayStart = new Date(
      now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0
    )
    const yestStart = new Date(todayStart)
    yestStart.setDate(yestStart.getDate() - 1)
    const startOfToday = todayStart.toISOString()
    const startOfYesterday = yestStart.toISOString()

    try {
      const [itemsRes, settingsRes, todayRes, yRes, savingsRes, eventsRes] = await Promise.all([
        supabase
          .from('items')
          .select('id,name,category,unit,cost_per_unit')
          .eq('active', true)
          .order('name'),
        supabase
          .from('settings')
          .select('daily_target,large_entry_threshold')
          .maybeSingle(),
        supabase
          .from('waste_logs')
          .select('*')
          .gte('logged_at', startOfToday)
          .order('logged_at', { ascending: false }),
        supabase
          .from('waste_logs')
          .select('total_cost')
          .gte('logged_at', startOfYesterday)
          .lt('logged_at', startOfToday),
        // vw_savings_summary always returns one row (aggregates with no
        // group-by). Even on an empty savings_events table, we get zeros.
        supabase
          .from('vw_savings_summary')
          .select('*')
          .single(),
        // All savings_events for the breakdown sheet + attestation modal.
        // Both are derived from this single fetch via useMemo below.
        supabase
          .from('savings_events')
          .select('id, source, category, dollar_amount, calculation_method, occurred_at, owner_confirmed, owner_confirmed_at, evidence_json')
          .order('occurred_at', { ascending: false })
          .limit(500),
      ])

      if (itemsRes.error) throw itemsRes.error
      if (todayRes.error) throw todayRes.error
      if (yRes.error) throw yRes.error
      // settingsRes / savingsRes / eventsRes can fail silently —
      // fall back to defaults so the screen never goes blank on a
      // transient view error.

      setItems(itemsRes.data ?? [])
      if (settingsRes.data) setSettings(settingsRes.data)
      setTodayLogs(todayRes.data ?? [])
      setYesterdayTotal(
        (yRes.data ?? []).reduce((s, r) => s + Number(r.total_cost || 0), 0)
      )
      if (savingsRes.data) setSavingsSummary(savingsRes.data)
      setSavingsEvents(eventsRes.data ?? [])
    } catch (e) {
      setError(e)
      // eslint-disable-next-line no-console
      console.error('[useWasteData]', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // Auto-refresh at kitchen-day rollover so a tablet left on overnight
  // shows fresh "today" data when the manager comes in. Polls once per
  // minute (cheap) and only triggers a refetch when the local calendar
  // date actually changes.
  useEffect(() => {
    let lastDate = new Date().toDateString()
    const id = setInterval(() => {
      const today = new Date().toDateString()
      if (today !== lastDate) {
        lastDate = today
        fetchAll()
      }
    }, 60_000)
    return () => clearInterval(id)
  }, [fetchAll])

  // Refresh when the tab becomes visible again — covers the common
  // "phone in pocket overnight, manager picks it up at 7am" case.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') {
        fetchAll()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchAll])

  const todayTotal = useMemo(
    () => todayLogs.reduce((s, l) => s + Number(l.total_cost || 0), 0),
    [todayLogs]
  )

  // Recompute the savings_events row for a given kitchen day.
  // Called after waste_log inserts/deletes so the LifetimeSavings
  // tile stays in sync with the actual waste totals on screen.
  //
  // Date param: a Date or anything Date can parse. Defaults to "now"
  // in America/Chicago (the kitchen's timezone). 'en-CA' locale gives
  // a YYYY-MM-DD string which Postgres parses as a date directly.
  const recomputeSavings = useCallback(async (whenTs) => {
    const d = whenTs ? new Date(whenTs) : new Date()
    const kitchenDate = d.toLocaleDateString('en-CA', {
      timeZone: 'America/Chicago',
    })
    const { error: rpcErr } = await supabase.rpc('recompute_daily_savings', {
      target_date: kitchenDate,
    })
    if (rpcErr) {
      // Don't blow up the calling flow — log and continue. The
      // nightly cron will reconcile this if recompute fails.
      // eslint-disable-next-line no-console
      console.error('[recomputeSavings]', rpcErr)
    }
  }, [])

  // Owner attestation: bulk-confirm every currently unconfirmed savings
  // event. Used by the attestation modal when the owner says "yes, this
  // matches my books." Confirmed events flip to "verified" and stop
  // counting toward the "preliminary, pending review" tag on the tile.
  // See Instructions Claude.md §7e.
  //
  // Kept for backward compatibility; the modal now uses
  // reviewSavingsEvents which supports per-row confirm / edit / dismiss.
  const confirmAllSavings = useCallback(async () => {
    const { error: updErr } = await supabase
      .from('savings_events')
      .update({
        owner_confirmed: true,
        owner_confirmed_at: new Date().toISOString(),
      })
      .eq('owner_confirmed', false)
    if (updErr) throw updErr
    await fetchAll()
  }, [fetchAll])

  // Per-row owner attestation: applies a decision to each event in the
  // batch. Decisions is shaped { [eventId]: { action, amount } } where
  // action is one of:
  //   'confirm' — keep the event at its current dollar_amount, mark verified
  //   'edit'    — update dollar_amount to the supplied amount, mark verified
  //   'dismiss' — zero out dollar_amount (audit row stays for history),
  //               still mark verified so it stops appearing in the
  //               unconfirmed queue
  //
  // We run all updates in parallel — typical batch is 5–30 rows, so a
  // single round-trip per row is fine. If volume ever grows past ~100,
  // swap this for an RPC that does a CASE-based bulk update.
  const reviewSavingsEvents = useCallback(async (decisions) => {
    const ids = Object.keys(decisions || {})
    if (ids.length === 0) return { confirmed: 0, edited: 0, dismissed: 0, total: 0 }

    const stamp = new Date().toISOString()
    const updates = ids.map((id) => {
      const d = decisions[id]
      const action = d?.action || 'confirm'
      const amount =
        action === 'dismiss'
          ? 0
          : Number(d?.amount ?? 0)
      return supabase
        .from('savings_events')
        .update({
          owner_confirmed: true,
          owner_confirmed_at: stamp,
          dollar_amount: amount,
        })
        .eq('id', id)
    })
    const results = await Promise.all(updates)
    const firstErr = results.find((r) => r.error)
    if (firstErr) throw firstErr.error

    // Tally for the toast.
    let confirmed = 0, edited = 0, dismissed = 0, total = 0
    for (const id of ids) {
      const d = decisions[id]
      const action = d?.action || 'confirm'
      if (action === 'dismiss') {
        dismissed += 1
      } else {
        total += Number(d?.amount ?? 0)
        if (action === 'edit') edited += 1
        else confirmed += 1
      }
    }

    await fetchAll()
    return { confirmed, edited, dismissed, total }
  }, [fetchAll])

  // Hard-delete a waste log row. Used for the "remove accidental entry"
  // flow on the recent entries list. After delete, recompute the
  // affected kitchen day's savings event and refresh the dashboard
  // so today's total + top-3 + savings tile all stay consistent.
  //
  // log: full waste_log object (must include id and logged_at).
  const deleteLog = useCallback(async (log) => {
    const { error: delErr } = await supabase
      .from('waste_logs')
      .delete()
      .eq('id', log.id)
    if (delErr) throw delErr
    await recomputeSavings(log.logged_at)
    await fetchAll()
  }, [fetchAll, recomputeSavings])

  // Unconfirmed events drive the attestation modal. Derived from the
  // single savings_events fetch so we don't hit the DB twice.
  const unconfirmedEvents = useMemo(
    () => savingsEvents.filter((e) => !e.owner_confirmed),
    [savingsEvents]
  )

  // Top 3 wasted items today, aggregated by item_name and ranked by $ cost.
  const top3 = useMemo(() => {
    const map = new Map()
    for (const l of todayLogs) {
      const prev = map.get(l.item_name) || { cost: 0, qty: 0, unit: l.unit }
      map.set(l.item_name, {
        cost: prev.cost + Number(l.total_cost || 0),
        qty: prev.qty + Number(l.quantity || 0),
        unit: l.unit,
      })
    }
    return [...map.entries()]
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 3)
  }, [todayLogs])

  return {
    items,
    settings,
    todayLogs,
    todayTotal,
    yesterdayTotal,
    top3,
    savingsSummary,
    savingsEvents,
    unconfirmedEvents,
    loading,
    error,
    refresh: fetchAll,
    deleteLog,
    recomputeSavings,
    confirmAllSavings,
    reviewSavingsEvents,
  }
}
